// Material Hierarchy - Detailed material types with quality restrictions
// For serious custom clothing designers who need specific materials

export const MATERIAL_HIERARCHY = {
  'Cotton': {
    baseQuality: 'budget', // Minimum quality needed
    variants: {
      'Standard Cotton': {
        quality: 'budget',
        description: 'Basic cotton fabric, affordable and versatile',
        properties: 'Soft, breathable, standard weight',
        bestFor: 'Everyday wear, casual designs'
      },
      'Organic Cotton': {
        quality: 'mid-range',
        description: 'Eco-friendly organic cotton, sustainably sourced',
        properties: 'Softer feel, natural, environmentally conscious',
        bestFor: 'Sustainable fashion, elevated casual wear'
      },
      'Pima Cotton': {
        quality: 'premium',
        description: 'Long-staple cotton, ultra-soft and durable',
        properties: 'Extra soft, longer fibers, superior quality',
        bestFor: 'High-end t-shirts, luxury basics'
      },
      'Egyptian Cotton': {
        quality: 'luxury',
        description: 'Luxury long-staple cotton, finest quality available',
        properties: 'Ultra-soft, silky feel, exceptional durability',
        bestFor: 'Luxury garments, high-end collections',
        locked: true // Requires luxury tier
      },
      'Sea Island Cotton': {
        quality: 'luxury',
        description: 'Rarest cotton variety, ultimate luxury',
        properties: 'Silky smooth, extremely long fibers, high-end',
        bestFor: 'Ultra-luxury pieces, exclusive designs',
        locked: true
      }
    }
  },
  'Leather': {
    baseQuality: 'mid-range',
    variants: {
      'Genuine Leather': {
        quality: 'mid-range',
        description: 'Real leather, standard quality',
        properties: 'Durable, natural grain, classic look',
        bestFor: 'Casual jackets, accessories'
      },
      'Top Grain Leather': {
        quality: 'premium',
        description: 'High-quality leather with natural surface',
        properties: 'Durable, refined grain, rich feel',
        bestFor: 'High-end jackets, quality accessories'
      },
      'Full Grain Leather': {
        quality: 'premium',
        description: 'Highest quality leather, natural grain intact',
        properties: 'Most durable, develops patina, high-end',
        bestFor: 'High-end jackets, luxury accessories'
      },
      'Italian Leather': {
        quality: 'luxury',
        description: 'Italian-tanned leather, exceptional quality',
        properties: 'Buttery soft, rich texture, luxury finish',
        bestFor: 'Luxury jackets, formal and statement pieces',
        locked: true
      },
      'Premium Full-Grain Leather': {
        quality: 'luxury',
        description: 'Top-grade full-grain leather, exceptional craftsmanship',
        properties: 'Natural grain intact, develops a rich patina, luxury hand-feel',
        bestFor: 'Ultra-luxury jackets, exclusive one-of-one pieces',
        locked: true,
        premium: true
      }
    }
  },
  'Wool': {
    baseQuality: 'mid-range',
    variants: {
      'Standard Wool': {
        quality: 'mid-range',
        description: 'Quality wool fabric, warm and durable',
        properties: 'Warm, natural texture, good insulation',
        bestFor: 'Winter garments, coats'
      },
      'Merino Wool': {
        quality: 'premium',
        description: 'Fine merino wool, soft and luxurious',
        properties: 'Ultra-soft, fine fibers, luxurious feel',
        bestFor: 'High-end sweaters, luxury knitwear'
      },
      'Superfine Merino': {
        quality: 'premium',
        description: 'Ultra-fine merino wool, exceptional quality',
        properties: 'Extremely soft, lightweight, high-end',
        bestFor: 'High-end knitwear, luxury basics'
      },
      'Vicuña Wool': {
        quality: 'luxury',
        description: 'Rarest wool, ultimate luxury fiber',
        properties: 'Softest natural fiber, extremely rare, luxury',
        bestFor: 'Ultra-luxury pieces, exclusive collections',
        locked: true
      }
    }
  },
  'Silk': {
    baseQuality: 'premium',
    variants: {
      'Silk': {
        quality: 'premium',
        description: 'Luxurious silk fabric, elegant and smooth',
        properties: 'Smooth, elegant drape, luxurious feel',
        bestFor: 'Formal wear, luxury garments'
      },
      'Mulberry Silk': {
        quality: 'premium',
        description: 'Mulberry silk, finest quality',
        properties: 'Ultra-smooth, finest fibers, high-end',
        bestFor: 'High-end formal wear, luxury pieces'
      },
      'Charmeuse Silk': {
        quality: 'luxury',
        description: 'Luxury charmeuse silk, ultra-luxurious',
        properties: 'Glossy finish, elegant drape, luxury',
        bestFor: 'Ultra-luxury formal wear, statement pieces',
        locked: true
      },
      'Wild Silk': {
        quality: 'luxury',
        description: 'Rare wild silk, exclusive luxury',
        properties: 'Natural texture, rare, exclusive',
        bestFor: 'Ultra-exclusive pieces, limited editions',
        locked: true
      }
    }
  },
  'Denim': {
    baseQuality: 'budget',
    variants: {
      'Standard Denim': {
        quality: 'budget',
        description: 'Classic denim fabric, durable and versatile',
        properties: 'Durable, classic texture, versatile',
        bestFor: 'Casual jeans, everyday wear'
      },
      'Selvedge Denim': {
        quality: 'premium',
        description: 'Selvedge denim, traditional craftsmanship',
        properties: 'Dense weave, high quality, traditional',
        bestFor: 'High-end jeans, quality denim pieces'
      },
      'Japanese Selvedge': {
        quality: 'luxury',
        description: 'Luxury Japanese selvedge, finest denim',
        properties: 'Top-grade, exceptional quality, luxury',
        bestFor: 'Luxury denim, exclusive pieces',
        locked: true
      }
    }
  },
  'Cashmere': {
    baseQuality: 'premium',
    variants: {
      'Cashmere': {
        quality: 'premium',
        description: 'Luxurious cashmere, ultra-soft and warm',
        properties: 'Ultra-soft, warm, luxurious feel',
        bestFor: 'High-end sweaters, luxury knitwear'
      },
      'Grade A Cashmere': {
        quality: 'luxury',
        description: 'Highest grade cashmere, ultimate luxury',
        properties: 'Finest fibers, ultra-soft, exceptional',
        bestFor: 'Ultra-luxury knitwear, exclusive pieces',
        locked: true
      },
      'Mongolian Cashmere': {
        quality: 'luxury',
        description: 'Mongolian cashmere, rarest quality',
        properties: 'Longest fibers, softest, most luxurious',
        bestFor: 'Ultra-exclusive pieces, limited editions',
        locked: true
      }
    }
  },
  'Linen': {
    baseQuality: 'mid-range',
    variants: {
      'Standard Linen': {
        quality: 'mid-range',
        description: 'Natural linen fabric, breathable and cool',
        properties: 'Breathable, natural texture, cool',
        bestFor: 'Summer wear, casual designs'
      },
      'Belgian Linen': {
        quality: 'premium',
        description: 'Belgian linen, finest quality',
        properties: 'Smoother, finer texture, high-end',
        bestFor: 'High-end summer wear, luxury casual'
      },
      'Irish Linen': {
        quality: 'luxury',
        description: 'Luxury Irish linen, exceptional quality',
        properties: 'Ultra-fine, smooth, luxury',
        bestFor: 'Ultra-luxury casual, exclusive pieces',
        locked: true
      }
    }
  },
  'Polyester': {
    baseQuality: 'budget',
    variants: {
      'Standard Polyester': {
        quality: 'budget',
        description: 'Synthetic polyester, durable and affordable',
        properties: 'Durable, moisture-wicking, affordable',
        bestFor: 'Activewear, budget-friendly designs'
      },
      'Performance Polyester': {
        quality: 'mid-range',
        description: 'Enhanced performance polyester, technical fabric',
        properties: 'Moisture-wicking, quick-dry, technical',
        bestFor: 'Athletic wear, performance clothing'
      },
      'Recycled Polyester': {
        quality: 'premium',
        description: 'Eco-friendly recycled polyester, sustainable',
        properties: 'Sustainable, high-performance, high-end',
        bestFor: 'Sustainable activewear, eco-conscious designs'
      }
    }
  },
  'Wool': {
    baseQuality: 'mid-range',
    variants: {
      'Standard Wool': {
        quality: 'mid-range',
        description: 'Quality wool fabric, warm and durable',
        properties: 'Warm, natural texture, good insulation',
        bestFor: 'Winter garments, coats'
      },
      'Merino Wool': {
        quality: 'premium',
        description: 'Fine merino wool, soft and luxurious',
        properties: 'Ultra-soft, fine fibers, luxurious feel',
        bestFor: 'High-end sweaters, luxury knitwear'
      },
      'Superfine Merino': {
        quality: 'premium',
        description: 'Ultra-fine merino wool, exceptional quality',
        properties: 'Extremely soft, lightweight, high-end',
        bestFor: 'High-end knitwear, luxury basics'
      },
      'Vicuña Wool': {
        quality: 'luxury',
        description: 'Rarest wool, ultimate luxury fiber',
        properties: 'Softest natural fiber, extremely rare, luxury',
        bestFor: 'Ultra-luxury pieces, exclusive collections',
        locked: true
      }
    }
  }
}

/** Old customer-facing names → generic luxury names (never show brand names). */
const MATERIAL_VARIANT_ALIASES = {
  'Chrome Hearts Grade Leather': 'Premium Full-Grain Leather'
}

/** Customer-facing names for quality keys; "Premium" is reserved for the printed tier. */
export const QUALITY_LABELS = {
  budget: 'budget',
  'mid-range': 'mid-range',
  premium: 'high-end',
  luxury: 'luxury'
}

export function canonicalizeMaterialVariant(variantName) {
  if (!variantName) return variantName
  return MATERIAL_VARIANT_ALIASES[variantName] || variantName
}

/** Extra Custom-tier fabrics that are not yet in MATERIAL_HIERARCHY (no gated variants). */
export const CUSTOM_EXTRA_MATERIALS = [
  'Suede',
  'Velvet',
  'Fleece',
  'Mesh',
  'Canvas',
  'Tweed',
  'Alpaca',
  'Mohair',
  'Tencel'
]

/** Full Custom-tier material list — every hierarchy category plus extras, ungated. */
export function getCustomMaterialCategories() {
  return [...new Set([...Object.keys(MATERIAL_HIERARCHY), ...CUSTOM_EXTRA_MATERIALS])].sort()
}

/** All variants for a material, including luxury/locked — Custom does not gate by quality. */
export function getAllMaterialVariants(materialType) {
  const material = MATERIAL_HIERARCHY[materialType]
  if (!material?.variants) return []
  return Object.keys(material.variants)
}

// Get available material variants based on quality level
export function getAvailableMaterials(quality = 'budget') {
  const qualityOrder = ['budget', 'mid-range', 'premium', 'luxury']
  const qualityIndex = qualityOrder.indexOf(quality)
  
  const available = {}
  
  Object.keys(MATERIAL_HIERARCHY).forEach(materialType => {
    const variants = MATERIAL_HIERARCHY[materialType].variants
    const availableVariants = {}
    
    Object.keys(variants).forEach(variantName => {
      const variant = variants[variantName]
      const variantQualityIndex = qualityOrder.indexOf(variant.quality)
      
      // Material is available if user's quality level is equal or higher
      if (variantQualityIndex <= qualityIndex && !variant.locked) {
        availableVariants[variantName] = variant
      } else if (variant.locked && qualityIndex >= 3) {
        // Locked materials only available at luxury
        availableVariants[variantName] = variant
      }
    })
    
    if (Object.keys(availableVariants).length > 0) {
      available[materialType] = availableVariants
    }
  })
  
  return available
}

// Check if material variant requires upgrade
export function requiresUpgrade(materialType, variantName, currentQuality) {
  const material = MATERIAL_HIERARCHY[materialType]
  if (!material) return false
  
  const variant = material.variants[canonicalizeMaterialVariant(variantName)]
  if (!variant) return false
  
  const qualityOrder = ['budget', 'mid-range', 'premium', 'luxury']
  const currentIndex = qualityOrder.indexOf(currentQuality)
  const requiredIndex = qualityOrder.indexOf(variant.quality)
  
  // If variant is locked or requires higher quality
  if (variant.locked && currentIndex < 3) {
    return { required: 'luxury', message: 'Luxury quality required for this material' }
  }
  
  if (requiredIndex > currentIndex) {
    return { 
      required: variant.quality, 
      message: `${variant.quality.charAt(0).toUpperCase() + variant.quality.slice(1)} quality required for ${variantName}` 
    }
  }
  
  return false
}

// Get material variant details
export function getMaterialVariant(materialType, variantName) {
  const name = canonicalizeMaterialVariant(variantName)
  return MATERIAL_HIERARCHY[materialType]?.variants[name] || null
}

